/**
 * The admin page's Privacy tab (L3): the two switches that decide what leaves this org for the shared foundry
 * knowledge, both off until an org admin gives them. PrivacyView is the pure view; PrivacyPanel reads and changes the
 * org's own part (/v1/twin/knowledge-sharing).
 *
 * EVERY SENTENCE BELOW IS A PROMISE A CUSTOMER READS, and holds only what the code keeps (the review of #1128, MEDIUM 3):
 *  - an option off withdraws the org's contributions from what is still under review (open and accepted candidates, and a
 *    rejected one's word), never what staff have already added to the foundry pack;
 *  - a word that carries the name of an org on the platform, or of the org's own products, plants, sites or people, is
 *    left out; a customer that is none of these cannot be recognised, so no sentence says "never a customer's name";
 *  - staff see which orgs contributed a candidate (the console names them), and the pack states plants only as counts;
 *  - a word with a digit is refused, and nothing is accepted for the pack until 3 plants share it.
 * No count of what the org shares is shown: the reply carries none (it was a probe oracle across orgs).
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { cn } from "@/lib/ui/cn";
import { isLiveApi } from "@/lib/api";
import { knowledgeSharingApi, type KnowledgeSharing, type KnowledgeSharingSetting } from "@/lib/api/knowledge-candidates";
import { useOrg } from "@/lib/org-context";
import { setSharing, sharingErrorText } from "./knowledge-sharing";

interface SwitchRowProps {
  label: string;
  setting: KnowledgeSharingSetting;
  what: string;
  sharing: KnowledgeSharing;
  canAdmin: boolean;
  held: boolean;
  onToggle: (setting: KnowledgeSharingSetting, on: boolean) => void;
}

function SwitchRow({ label, setting, what, sharing, canAdmin, held, onToggle }: SwitchRowProps) {
  const on = setting === "counts" ? sharing.share_knowledge_counts : sharing.share_knowledge_content;
  const locked = !canAdmin || held;
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-surface p-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm font-medium text-on-surface">{label}</span>
        <span className="text-xs text-on-surface-muted">{what}</span>
        {!canAdmin && <span className="text-[11px] text-on-surface-muted">Only org admins can change this.</span>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => onToggle(setting, !on)}
        disabled={locked}
        title={!canAdmin ? "Only org admins can change this." : on ? "Turn off: withdraw your contributions still under review" : "Turn on"}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
          on ? "bg-brand" : "bg-surface-muted",
          locked && "cursor-not-allowed opacity-60",
        )}
      >
        <span
          className={cn(
            "inline-block size-4 transform rounded-full bg-surface shadow transition-transform",
            on ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}

export interface PrivacyViewProps {
  /** The org's own part; absent while it loads or when it did not read. */
  sharing: KnowledgeSharing | undefined;
  loading: boolean;
  error?: string;
  canAdmin: boolean;
  /** The setting being saved, whose switch is held until it answers. */
  busy: KnowledgeSharingSetting | null;
  onToggle: (setting: KnowledgeSharingSetting, on: boolean) => void;
}

/** The Privacy tab's content. */
export function PrivacyView({ sharing, loading, error, canAdmin, busy, onToggle }: PrivacyViewProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
          <EyeOff className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle>What this plant shares</CardTitle>
          <CardDescription>
            Nothing from your plant is added to the shared foundry knowledge unless an org admin switches the option on.
            Each option can be turned off again at any time, and turning it off withdraws your contributions from what is
            still under review. Whatever Sahas staff have already added to the foundry pack stays in it.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {loading && !sharing && <p className="text-sm text-on-surface-muted">Reading what this org shares...</p>}
        {!loading && !sharing && (
          <p className="text-sm text-on-surface-muted">What this org shares could not be read. Reload the page.</p>
        )}
        {sharing && (
          <>
            <SwitchRow
              label="Share counts"
              setting="counts"
              what="How many plants' data bear a hypothesis out, find against it or do not bear it out, and how a recommendation you tried turned out (improved, no clear change or worse). A count holds the hypothesis or the recommendation and a verdict, nothing else: never a rate, an effect size or a figure. The key of a recommendation can carry the names of your own parameters or inputs. Sahas staff can see which orgs contributed a count; the foundry pack states only how many plants."
              sharing={sharing}
              canAdmin={canAdmin}
              held={busy === "counts"}
              onToggle={onToggle}
            />
            <SwitchRow
              label="Share words and names"
              setting="content"
              what="The words you approve for defects and parameters in your vocabulary, the defect words an import finds no home for, and the names, units and data kinds of the parameters your imports add. Words and names only: a word with a digit in it is refused. A word that carries the name of an org on the platform, or of one of your own products, plants, sites or people, is left out. A name this check cannot know, such as a customer that is no org here and is in none of your product names, is not left out, so check the words you approve. Sahas staff can see which orgs contributed a word."
              sharing={sharing}
              canAdmin={canAdmin}
              held={busy === "content"}
              onToggle={onToggle}
            />
            <p className="text-xs text-on-surface-muted">
              Sahas staff add what several plants share to the foundry pack by hand, and only once at least{" "}
              {Math.max(sharing.min_plants_to_shape, sharing.min_plants_content)} plants share it: what fewer plants share
              can be seen by staff and is accepted for nothing. A word or a count already added to the pack stays in it when
              you turn an option off.
            </p>
          </>
        )}
        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** The Privacy tab: the org's own part, read and changed through the org's own route. */
export function PrivacyPanel() {
  const { activeOrgId, canAdmin } = useOrg();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<KnowledgeSharingSetting | null>(null);
  const [error, setError] = useState<string | undefined>();
  const key = ["knowledge-sharing", activeOrgId] as const;
  const sharing = useQuery({
    queryKey: key,
    queryFn: () => knowledgeSharingApi.get(),
    enabled: Boolean(activeOrgId) && isLiveApi,
  });
  const toggle = async (setting: KnowledgeSharingSetting, on: boolean) => {
    setBusy(setting);
    setError(undefined);
    try {
      qc.setQueryData(key, await setSharing(setting, on));
    } catch (err) {
      setError(sharingErrorText(err));
    } finally {
      setBusy(null);
    }
  };
  return (
    <PrivacyView
      sharing={sharing.data}
      loading={sharing.isLoading}
      error={error}
      canAdmin={canAdmin}
      busy={busy}
      onToggle={(setting, on) => void toggle(setting, on)}
    />
  );
}
